import { ConstraintContainer } from 'src/components/common/ConstraintContainer';
import { ProductDTO } from 'src/models/pim';
import styled from 'styled-components';
import { media } from 'src/styles/designs';
import { Heading } from 'src/components/common/Heading';
import { DictionaryTitlesCode } from 'src/models/pim/dictionaries';
import { FC, useMemo } from 'react';
import { ProductAttributeCode } from 'src/models/pim/attributes';
import { useProductAttributes } from 'src/modules/products/hooks/useProductAttributes';
import { head } from 'lodash';
import { TechnicalFeatureItem } from './TechnicalFeatureItem';
import { SwiperSlide } from 'swiper/react';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';
import { useTitlesDictionary } from 'src/modules/products/hooks/useTitlesDictionary';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';
import { ContentSwiper } from 'src/components/common/Swipers/ContentSwiper';
import { NavigationButton } from 'src/modules/materials/components/ShowcasePage/common';
import { Paragraph } from 'src/components/common/typography/Text';

const Section = styled.section`
  padding: 32px 0;
  overflow: hidden;

  ${media.w.greaterThanOrEqual.desktop} {
    padding: 56px 0 86px;
  }
`;

const SwiperContainer = styled(ContentSwiper)`
  justify-content: flex-start;

  &&& {
    ${NavigationButton} {
      top: 50%;
      transform: translateY(-50%);
      margin: 0;
      display: flex !important;
    }
  }

  .swiper-wrapper {
    gap: 100px;
  }

  .swiper-slide {
    width: min-content;
  }

  ${media.w.lessThan.desktop} {
    display: none;
  }
`;

const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;

  ${media.w.greaterThanOrEqual.desktop} {
    display: none;
  }
`;

const Description = styled(Paragraph)`
  margin-bottom: 32px;
  max-width: 870px;

  ${media.w.greaterThanOrEqual.desktop} {
    margin-bottom: 48px;
  }
`;

export const ProductPageTechnicalFeatures: FC<ProductDTO> = (product) => {
  const translateFn = useTranslateItem();

  const { getProductAttribute, getMultipleProductAttributes } =
    useProductAttributes(product.attributes);

  const [title] = useTitlesDictionary(DictionaryTitlesCode.TechnicalFeatures);

  const description = head(
    getProductAttribute(ProductAttributeCode.TechnicalFeaturesAndHandpieceText),
  );

  const items = useMemo(
    () =>
      getMultipleProductAttributes(
        ProductAttributeCode.TechnicalFeaturesAndHandpieceGroup,
      ).map((attributes) => ({
        title: head(
          getProductAttribute(
            ProductAttributeCode.TechnicalFeaturesAndHandpieceGroupTitle,
            attributes,
          ),
        ),
        description: head(
          getProductAttribute(
            ProductAttributeCode.TechnicalFeaturesAndHandpieceGroupText,
            attributes,
          ),
        ),
        image: head(
          getProductAttribute(
            ProductAttributeCode.TechnicalFeaturesAndHandpieceGroupImage,
            attributes,
          ),
        ),
      })),
    [getProductAttribute, getMultipleProductAttributes],
  );

  return (
    <Section>
      <ConstraintContainer>
        {title && (
          <Heading>
            <Markdown>{translateFn(title)?.value}</Markdown>
          </Heading>
        )}

        {description && (
          <Paragraph>
            <Description>{translateFn(description).value}</Description>
          </Paragraph>
        )}

        <SwiperContainer>
          {items.map((item, index) => (
            <SwiperSlide key={index}>
              <TechnicalFeatureItem {...item} />
            </SwiperSlide>
          ))}
        </SwiperContainer>

        <MobileContainer>
          {items.map((item, index) => (
            <TechnicalFeatureItem key={index} {...item} />
          ))}
        </MobileContainer>
      </ConstraintContainer>
    </Section>
  );
};
